import {
    Component,
    Element,
    Event,
    EventEmitter,
    forceUpdate,
    Fragment,
    h,
    Host,
    Listen,
    Prop,
    State,
} from "@stencil/core";
import { debounce } from "../../utils/debounce";
import { i18nProvider } from "@trumpf/ux.i18n";

@Component({
    tag: "ux-iws-horizontal-scroll-slider",
    styleUrl: "horizontal-scroll-slider.scss",
    shadow: true,
})
export class HorizontalScrollSlider {
    @Element() host: HTMLUxIwsHorizontalScrollSliderElement;

    /** Indicates that the arrow buttons should not be rendered */
    @Prop() noArrows: boolean = false;

    @State() canScrollLeft: boolean = false;
    @State() canScrollRight: boolean = true;

    @Listen("uxShow")
    handleUxShow(e: CustomEvent<{ animationDuration: number }>) {
        const { animationDuration } = e.detail;

        setTimeout(() => {
            this.scrollIntoView(e.target as HTMLElement);
        }, animationDuration);
    }

    /** Event emitted when the slider scrolls to next or previous slide */
    @Event() slideChange: EventEmitter<{ direction: "left" | "right" }>;

    private scrollContainer: HTMLElement;
    private i18nSubscriberId: string;
    private scrollListener: () => void = debounce(() => {
        this.updateScrollState();
    }, 50);

    private getItems(): HTMLElement[] {
        const items = this.host.querySelectorAll<HTMLElement>("[data-slide]");
        if (items.length > 0) {
            return Array.from(items);
        }
        return Array.from(this.host.children) as HTMLElement[];
    }

    private updateScrollState(): void {
        if (!this.scrollContainer) return;

        const { scrollLeft, scrollWidth, clientWidth } = this.scrollContainer;
        this.canScrollLeft = scrollLeft > 0;
        this.canScrollRight = scrollLeft < scrollWidth - clientWidth;
    }

    private findNextPartiallyVisibleElement(
        direction: "left" | "right"
    ): HTMLElement | null {
        if (!this.scrollContainer) return null;

        const containerRect = this.scrollContainer.getBoundingClientRect();
        const children = this.getItems();

        for (const child of children) {
            const childRect = child.getBoundingClientRect();

            if (direction === "right") {
                // Find first element that's partially or completely outside the right edge
                if (
                    Math.floor(childRect.right) >
                    Math.floor(containerRect.right)
                ) {
                    return child;
                }
            } else {
                // Find last element that's partially or completely outside the left edge
                if (Math.ceil(childRect.left) < Math.ceil(containerRect.left)) {
                    continue;
                }
                // Return the previous element if it exists
                const currentIndex = children.indexOf(child);
                return currentIndex > 0 ? children[currentIndex - 1] : null;
            }
        }

        return null;
    }

    private scrollLeft = (): void => {
        this.scrollContainer.scrollTo({
            left:
                this.scrollContainer.scrollLeft -
                0.75 * this.scrollContainer.clientWidth,
            top: this.scrollContainer.scrollTop,
        });
    };

    private scrollRight = (): void => {
        this.scrollContainer.scrollTo({
            left:
                this.scrollContainer.scrollLeft +
                0.75 * this.scrollContainer.clientWidth,
            top: this.scrollContainer.scrollTop,
        });
    };

    private scrollToNext = (): void => {
        if (!this.canScrollRight) return;

        const nextElement = this.findNextPartiallyVisibleElement("right");
        if (nextElement) {
            this.scrollIntoView(nextElement);
            return;
        }
        this.scrollRight();
    };

    private scrollToPrevious = (): void => {
        if (!this.canScrollLeft) return;

        const prevElement = this.findNextPartiallyVisibleElement("left");
        if (prevElement) {
            this.scrollIntoView(prevElement);
            return;
        }
        this.scrollLeft();
    };

    private scrollIntoView(element: HTMLElement): void {
        const prefersReducedMotion = window.matchMedia(
            "(prefers-reduced-motion: reduce)"
        ).matches;
        element?.scrollIntoView({
            behavior: prefersReducedMotion ? "instant" : "smooth",
            block: "nearest",
            inline: "start",
        });
    }

    private handleScrollContainerRef(el: HTMLElement): void {
        this.scrollContainer?.removeEventListener(
            "scroll",
            this.scrollListener
        );
        this.scrollContainer = el;
        el?.addEventListener("scroll", this.scrollListener);
    }

    connectedCallback() {
        this.i18nSubscriberId = i18nProvider.subscribe(() => forceUpdate(this));
    }

    disconnectedCallback() {
        this.scrollContainer?.removeEventListener(
            "scroll",
            this.scrollListener
        );
        i18nProvider.unsubscribe(this.i18nSubscriberId);
    }

    componentDidLoad() {
        this.updateScrollState();
    }

    render() {
        const { t } = i18nProvider;
        return (
            <Host>
                <div class="slider-container">
                    <div
                        class={{
                            "gradient-overlay": true,
                            "gradient-left": true,
                            visible: this.canScrollLeft,
                        }}
                        role="button"
                        tabIndex={this.noArrows && this.canScrollLeft ? 0 : -1}
                        aria-label={t("slider.prevButton.label")}
                        onClick={
                            this.canScrollLeft
                                ? this.scrollToPrevious
                                : undefined
                        }
                    ></div>
                    <div
                        class={{
                            "gradient-overlay": true,
                            "gradient-right": true,
                            visible: this.canScrollRight,
                        }}
                        role="button"
                        tabIndex={this.noArrows && this.canScrollRight ? 0 : -1}
                        aria-label={t("slider.nextButton.label")}
                        onClick={
                            this.canScrollRight ? this.scrollToNext : undefined
                        }
                    />
                    {!this.noArrows && (
                        <Fragment>
                            <button
                                class={{
                                    "nav-button": true,
                                    "nav-button-left": true,
                                    visible: this.canScrollLeft,
                                }}
                                onClick={this.scrollToPrevious}
                                aria-label={t("slider.prevButton.label")}
                                tabIndex={this.canScrollLeft ? 0 : -1}
                            >
                                <ux-icon name="chevron-left" weight="light" />
                            </button>
                            <button
                                class={{
                                    "nav-button": true,
                                    "nav-button-right": true,
                                    visible: this.canScrollRight,
                                }}
                                onClick={this.scrollToNext}
                                aria-label={t("slider.nextButton.label")}
                                tabIndex={this.canScrollRight ? 0 : -1}
                            >
                                <ux-icon name="chevron-right" weight="light" />
                            </button>
                        </Fragment>
                    )}
                    <div
                        class="scroll-container"
                        ref={(el) => this.handleScrollContainerRef(el)}
                    >
                        <div class="slides-wrapper">
                            <slot />
                        </div>
                    </div>
                </div>
            </Host>
        );
    }
}
